<template>
  <div class="certificate-container">
    <!-- 左侧导航 - 使用统一的侧边栏组件 -->
    <SidebarMenu />
    

    <!-- 右侧主内容 -->
    <div class="main-content">
      <div class="content-header">
        <h2>导入历史记录</h2>
        <el-button @click="handleLogout" type="text" style="margin-left: auto;">退出登录</el-button>
      </div>

    <!-- 搜索区域 -->
    <div class="search-area">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="操作人">
          <el-input
            v-model="searchForm.operatorName"
            placeholder="请输入操作人"
            style="width: 180px;"
          />
        </el-form-item>
        
        <el-form-item label="操作时间">
          <el-date-picker
            v-model="searchForm.timeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 400px;"
          />
        </el-form-item>
        
        <el-form-item label="文件名称">
          <el-input
            v-model="searchForm.fileName"
            placeholder="请输入文件名称"
            style="width: 200px;"
          />
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
          <el-button type="primary" plain @click="handleExport">导出Excel</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 表格区域 -->
    <div class="table-area">
      <el-table
        v-loading="loading"
        :data="historyList"
        style="width: 100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column type="index" label="序号" min-width="20"></el-table-column>
        <el-table-column prop="operatorName" label="操作人" width="120" align="center" />
        <el-table-column prop="operateTime" label="操作时间" width="200" align="center" />
        <el-table-column prop="fileName" label="文件名称" min-width="200" align="center" />
        <el-table-column prop="resolveStatusText" label="状态" width="100" align="center" />
        <el-table-column prop="successNum" label="成功条数" width="100" align="center" />
        <el-table-column prop="failNum" label="失败条数" width="100" align="center" />
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template #default="scope">
            <el-button
              type="primary"
              size="small"
              @click="handleDownload(scope.row.id, scope.row.fileName)"
            >
              下载
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页区域 -->
    <div class="pagination-area">
      <Pagination
      :current-page="pagination.page"
      :page-size="pagination.pageSize"
      :total="pagination.total"
      @current-change="handleCurrentChange"
      @size-change="handleSizeChange"
    />
    </div>

    <!-- 页脚 -->
    <Footer />
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import importHistoryAPI from '@/api/importHistory.js'
import SidebarMenu from '@/components/SidebarMenu.vue'
import Pagination from '@/components/Pagination.vue'
import Footer from '@/components/Footer.vue'

// 退出登录
const handleLogout = () => {
  localStorage.removeItem('isLoggedIn')
  localStorage.removeItem('token')
  window.location.href = '/'
}

// 搜索表单
const searchForm = reactive({
  operatorName: '',
  timeRange: [],
  fileName: ''
})

// 分页数据
const pagination = reactive({
  page: 1,
  pageSize: 10,
  total: 0
})

// 历史记录列表
const historyList = ref([])

// 加载状态
const loading = ref(false)

// 选中的行数据
const selectedRows = ref([])

// 处理选中行变化
const handleSelectionChange = (selection) => {
  selectedRows.value = selection
}

// 获取导入历史记录列表
const getImportHistory = async () => {
  loading.value = true
  try {
    // 构建请求参数，不直接展开searchForm以避免传入timeRange
    const params = {
      pageNo: pagination.page,
      pageSize: pagination.pageSize,
      operatorName: searchForm.operatorName,
      fileName: searchForm.fileName
    }

    // 如果有时间范围，需要转换格式并添加到params
    if (searchForm.timeRange && searchForm.timeRange.length === 2) {
      console.log('转换前的timeRange:', searchForm.timeRange);
      // 修复时间转换逻辑，保持当地时间不变
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };
      
      const startTime = formatDate(searchForm.timeRange[0]);
      const endTime = formatDate(searchForm.timeRange[1]);
      console.log('转换后的startTime:', startTime);
      console.log('转换后的endTime:', endTime);
      params.startTime = startTime;
      params.endTime = endTime;
    }

    console.log('最终构建的请求参数:', params);

    // 调用API获取数据
    // 响应拦截器已处理code=0的情况并直接返回data
    const result = await importHistoryAPI.getImportHistory(params)
    historyList.value = result.data || []
    pagination.total = parseInt(result.count) || 0
  } catch (error) {
    console.error('获取导入历史记录失败:', error)
    ElMessage.error('获取导入历史记录失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

// 查询
const handleSearch = () => {
  pagination.page = 1
  getImportHistory()
}

// 重置
const handleReset = () => {
  searchForm.operatorName = ''
  searchForm.timeRange = []
  searchForm.fileName = ''
  pagination.page = 1
  getImportHistory()
}

// 分页大小变化
const handleSizeChange = (size) => {
  pagination.pageSize = size
  getImportHistory()
}

// 当前页码变化
const handleCurrentChange = (current) => {
  pagination.page = current
  getImportHistory()
}

// 下载文件
const handleDownload = async (id, fileName) => {
  try {
    const response = await importHistoryAPI.downloadImportFile(id)
    
    // 创建下载链接
    const url = window.URL.createObjectURL(new Blob([response]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    
    // 清理
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('文件下载成功')
  } catch (error) {
    console.error('文件下载失败:', error)
    // 直接显示API返回的错误消息
    ElMessage.error(error.message || '文件下载失败，请稍后重试')
  }
}

// 导出Excel
const handleExport = async () => {
  try {
    console.log('开始导出操作');
    
    // 构建导出参数
    const exportParams = {
      operateorName: searchForm.operatorName || '',
      fileName: searchForm.fileName || '',
      // 修复idList处理，使用正确的字段名operateorId
      idList: selectedRows.value.length > 0 
        ? selectedRows.value.map(row => row.id).filter(id => id !== null && id !== undefined) 
        : []
    }
    
    // 如果有时间范围，需要转换格式
    if (searchForm.timeRange && searchForm.timeRange.length === 2) {
      // 修复时间转换逻辑，保持当地时间不变
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };
      
      exportParams.startTime = formatDate(searchForm.timeRange[0]);
      exportParams.endTime = formatDate(searchForm.timeRange[1]);
      console.log('导出转换后的startTime:', exportParams.startTime);
      console.log('导出转换后的endTime:', exportParams.endTime);
    } else {
      exportParams.startTime = '';
      exportParams.endTime = '';
    }
    
    console.log('导出参数:', exportParams);
    
    // 显示加载提示
    // ElMessage('正在准备导出数据，请稍候...');
    
    console.log('准备调用导出API');
    // 调用导出API - 现在API层已经处理了响应类型判断
    const blob = await importHistoryAPI.exportExcel(exportParams)
    
    // 检查响应是否有效
    if (!blob || blob.size === 0) {
      throw new Error('导出数据为空，请检查查询条件后重试')
    }
    
    console.log('导出成功，准备下载文件');
    
    // 创建下载链接
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // 设置文件名
    const timestamp = new Date().getTime()
    link.download = `导入历史记录_${timestamp}.xlsx`
    
    // 触发下载
    document.body.appendChild(link)
    link.click()
    
    // 清理
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
  } catch (error) {
    console.error('导出导入历史记录失败:', error);
    // 使用错误对象的message属性作为错误提示
    ElMessage.error(error.message || '导出失败，请稍后重试')
  }
}

// 组件挂载时获取数据
onMounted(() => {
  getImportHistory()
})
</script>

<style scoped>
/* 导入共享样式 */
@import '@/style/shared-styles.css';

/* 导入历史记录页面特有的样式 */
.search-area {
  padding: 20px 30px;
  background-color: var(--bg-primary);
  margin: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>